'use client';

import React from 'react';
import { decodePassphrase } from '@/lib/client-utils';
import { DebugMode } from '@/lib/Debug';
import { SettingsMenu } from '@/lib/SettingsMenu';
import { ConnectionDetails } from '@/lib/types';
import { ru } from '@/lib/ru';
import {
  formatChatMessageLinks,
  LocalUserChoices,
  PreJoin,
  RoomContext,
} from '@livekit/components-react';
import { AgimaRoomHeader } from '@/components/room/AgimaRoomHeader';
import { AgimaVideoConference } from '@/components/room/AgimaVideoConference';
import {
  ExternalE2EEKeyProvider,
  RoomOptions,
  VideoCodec,
  VideoPresets,
  Room,
  DeviceUnsupportedError,
  RoomConnectOptions,
  RoomEvent,
  TrackPublishDefaults,
  VideoCaptureOptions,
} from 'livekit-client';
import { useSetupE2EE } from '@/lib/useSetupE2EE';
import { useLowCPUOptimizer } from '@/lib/usePerfomanceOptimiser';
import { BackgroundPicker } from '@/components/room/BackgroundPicker';
import { OntologyPreJoin } from '@/components/OntologyPreJoin';
import { AgimaEntryShell, entryStyles } from '@/components/AgimaEntryShell';
import {
  createPublishProcessor,
  usePreviewBackgroundProcessor,
  type BackgroundProcessorInstance,
} from '@/lib/useCameraBackground';
import '@/lib/keep-prejoin-preview';

const CONN_DETAILS_ENDPOINT =
  process.env.NEXT_PUBLIC_CONN_DETAILS_ENDPOINT ?? '/api/connection-details';
const SHOW_SETTINGS_MENU = process.env.NEXT_PUBLIC_SHOW_SETTINGS_MENU == 'true';

type Whoami = {
  guest?: boolean;
  login?: string;
  name?: string;
  email?: string;
};

export function PageClientImpl(props: {
  roomName: string;
  region?: string;
  hq: boolean;
  codec: VideoCodec;
  singlePeerConnection: boolean;
  isGuest?: boolean;
}) {
  const isGuest = !!props.isGuest;
  const [ontologyReady, setOntologyReady] = React.useState(isGuest);
  const [whoami, setWhoami] = React.useState<Whoami | null>(null);
  const [whoamiReady, setWhoamiReady] = React.useState(false);
  const [authRequired, setAuthRequired] = React.useState(false);
  const [preJoinChoices, setPreJoinChoices] = React.useState<LocalUserChoices | undefined>(
    undefined,
  );
  const keycloakName = String(whoami?.name || whoami?.login || '').trim();
  const preJoinDefaults = React.useMemo(() => {
    return {
      username: isGuest ? '' : keycloakName,
      videoEnabled: true,
      audioEnabled: true,
    };
  }, [isGuest, keycloakName]);
  const [connectionDetails, setConnectionDetails] = React.useState<ConnectionDetails | undefined>(
    undefined,
  );
  // Created for the published track once the user joins, so the meeting starts
  // with the background already on.
  const [publishProcessor, setPublishProcessor] = React.useState<
    BackgroundProcessorInstance | undefined
  >(undefined);
  const preJoinRootRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    fetch(isGuest ? '/api/whoami?guest=1' : '/api/whoami', { credentials: 'same-origin' })
      .then((response) => {
        if (response.status === 401 && !isGuest) {
          // No SSO session: show the login-or-guest chooser instead of PreJoin.
          setAuthRequired(true);
          return { guest: false };
        }
        return response.ok ? response.json() : { guest: isGuest };
      })
      .then((data: Whoami) => setWhoami(data))
      .catch(() => setWhoami({ guest: isGuest }))
      .finally(() => setWhoamiReady(true));
  }, [isGuest]);

  React.useEffect(() => {
    if (isGuest || !preJoinRootRef.current) return;
    const input = preJoinRootRef.current.querySelector<HTMLInputElement>(
      'input[name="username"], input#username',
    );
    if (!input) return;
    input.readOnly = true;
    input.setAttribute('aria-readonly', 'true');
  }, [isGuest, whoamiReady, ontologyReady, preJoinChoices, connectionDetails]);

  const handlePreJoinSubmit = React.useCallback(
    async (values: LocalUserChoices) => {
      const username = isGuest ? values.username : keycloakName || values.username;
      const lockedValues = { ...values, username };
      setPreJoinChoices(lockedValues);
      const url = new URL(CONN_DETAILS_ENDPOINT, window.location.origin);
      url.searchParams.append('roomName', props.roomName);
      if (isGuest) {
        url.searchParams.append('guest', '1');
        url.searchParams.append('participantName', username);
      }
      if (props.region) {
        url.searchParams.append('region', props.region);
      }
      const connectionDetailsResp = await fetch(url.toString(), { credentials: 'same-origin' });
      if (!connectionDetailsResp.ok) {
        throw new Error(await connectionDetailsResp.text());
      }
      const connectionDetailsData = await connectionDetailsResp.json();
      setPublishProcessor(await createPublishProcessor());
      setConnectionDetails(connectionDetailsData);
    },
    [isGuest, keycloakName, props.region, props.roomName],
  );
  const handlePreJoinError = React.useCallback((e: any) => console.error(e), []);

  if (authRequired) {
    return <LoginChooser roomName={props.roomName} />;
  }

  if (!ontologyReady) {
    return (
      <AgimaEntryShell
        kicker={props.roomName}
        title="Папка ontology"
        subtitle="Выберите папку для обработки встречи или войдите без привязки."
        showAdminLink={!isGuest}
      >
        <OntologyPreJoin room={props.roomName} onDone={() => setOntologyReady(true)} />
      </AgimaEntryShell>
    );
  }

  if (!whoamiReady || connectionDetails === undefined || preJoinChoices === undefined) {
    return (
      <AgimaEntryShell
        kicker={props.roomName}
        title="Перед входом"
        subtitle={
          isGuest
            ? 'Укажите имя и проверьте камеру с микрофоном.'
            : 'Имя берётся из аккаунта AGIMA. Проверьте камеру и микрофон.'
        }
        prejoin
        showAdminLink={!isGuest}
      >
        {whoamiReady ? (
          <div
            ref={preJoinRootRef}
            className="agima-prejoin"
            data-lk-theme="default"
            data-octor-name-locked={isGuest ? undefined : 'true'}
          >
            <PreJoinWithBackgrounds
              defaults={preJoinDefaults}
              persistUserChoices={isGuest}
              userLabel={isGuest ? 'Ваше имя' : 'Имя из аккаунта AGIMA'}
              onSubmit={handlePreJoinSubmit}
              onError={handlePreJoinError}
            />
          </div>
        ) : (
          <p className={entryStyles.hint}>Загрузка…</p>
        )}
      </AgimaEntryShell>
    );
  }

  return (
    <main className="agima-room" data-lk-theme="default">
      <AgimaRoomHeader roomName={props.roomName} showAdminLink={!isGuest} />
      <VideoConferenceComponent
        connectionDetails={connectionDetails}
        userChoices={preJoinChoices}
        isGuest={isGuest}
        videoProcessor={publishProcessor}
        options={{
          codec: props.codec,
          hq: props.hq,
          singlePeerConnection: props.singlePeerConnection,
        }}
      />
    </main>
  );
}

/**
 * Shown on /<room> when there is no SSO session (replaces the old nginx
 * octor-login.html hack): sign in through Keycloak or continue as a guest.
 */
function PreJoinWithBackgrounds(props: {
  defaults: Partial<LocalUserChoices>;
  persistUserChoices: boolean;
  userLabel: string;
  onSubmit: (values: LocalUserChoices) => void;
  onError: (error: Error) => void;
}) {
  // Undefined until the user picks a background, so opening the page never pulls
  // the segmentation model; after that the same instance only switches modes.
  const previewProcessor = usePreviewBackgroundProcessor();

  return (
    <>
      <PreJoin
        defaults={props.defaults}
        persistUserChoices={props.persistUserChoices}
        joinLabel="Присоединиться"
        micLabel="Микрофон"
        camLabel="Камера"
        userLabel={props.userLabel}
        videoProcessor={previewProcessor}
        onSubmit={props.onSubmit}
        onError={props.onError}
      />
      {/* The card lays PreJoin out with `order`, so the picker sits right under
          the camera preview instead of below the join button. Spacing comes from
          the card's own `gap` — no margin here, or this row gets a double one. */}
      <div style={{ order: 2 }}>
        <BackgroundPicker showTitle compact />
      </div>
    </>
  );
}

function LoginChooser(props: { roomName: string }) {
  const room = encodeURIComponent(props.roomName);
  return (
    <AgimaEntryShell
      kicker={props.roomName}
      title="Вход во встречу"
      subtitle="Сотрудники входят через AGIMA. Гости — по ссылке, без корпоративного аккаунта."
    >
      <div className={entryStyles.stack}>
        <a
          className={entryStyles.primary}
          href={`/auth/login?rd=${encodeURIComponent(`/${props.roomName}`)}`}
        >
          Войти через AGIMA
        </a>
        <a className={entryStyles.secondary} href={`/guest/${room}`}>
          Продолжить как гость
        </a>
      </div>
    </AgimaEntryShell>
  );
}

function VideoConferenceComponent(props: {
  userChoices: LocalUserChoices;
  connectionDetails: ConnectionDetails;
  isGuest?: boolean;
  videoProcessor?: BackgroundProcessorInstance;
  options: {
    hq: boolean;
    codec: VideoCodec;
    singlePeerConnection: boolean;
  };
}) {
  const keyProvider = new ExternalE2EEKeyProvider();
  const { worker, e2eePassphrase } = useSetupE2EE();
  const e2eeEnabled = !!(e2eePassphrase && worker);

  const [e2eeSetupComplete, setE2eeSetupComplete] = React.useState(false);

  const roomOptions = React.useMemo((): RoomOptions => {
    let videoCodec: VideoCodec | undefined = props.options.codec ? props.options.codec : 'vp9';
    if (e2eeEnabled && (videoCodec === 'av1' || videoCodec === 'vp9')) {
      videoCodec = undefined;
    }
    const videoCaptureDefaults: VideoCaptureOptions = {
      deviceId: props.userChoices.videoDeviceId ?? undefined,
      resolution: props.options.hq ? VideoPresets.h2160 : VideoPresets.h720,
      // Publishing with the processor already attached means nobody ever sees the
      // real background, and there is no wait after joining.
      processor: props.videoProcessor,
    };
    const publishDefaults: TrackPublishDefaults = {
      dtx: false,
      videoSimulcastLayers: props.options.hq
        ? [VideoPresets.h1080, VideoPresets.h720]
        : [VideoPresets.h540, VideoPresets.h216],
      red: !e2eeEnabled,
      videoCodec,
    };
    return {
      videoCaptureDefaults: videoCaptureDefaults,
      publishDefaults: publishDefaults,
      audioCaptureDefaults: {
        deviceId: props.userChoices.audioDeviceId ?? undefined,
      },
      adaptiveStream: true,
      dynacast: true,
      e2ee: keyProvider && worker && e2eeEnabled ? { keyProvider, worker } : undefined,
      singlePeerConnection: props.options.singlePeerConnection,
    };
  }, [props.userChoices, props.options.hq, props.options.codec]);

  const room = React.useMemo(() => new Room(roomOptions), []);

  React.useEffect(() => {
    if (e2eeEnabled) {
      keyProvider
        .setKey(decodePassphrase(e2eePassphrase))
        .then(() => {
          room.setE2EEEnabled(true).catch((e) => {
            if (e instanceof DeviceUnsupportedError) {
              alert(ru.e2eeUnsupported);
              console.error(e);
            } else {
              throw e;
            }
          });
        })
        .then(() => setE2eeSetupComplete(true));
    } else {
      setE2eeSetupComplete(true);
    }
  }, [e2eeEnabled, room, e2eePassphrase]);

  const connectOptions = React.useMemo((): RoomConnectOptions => {
    return {
      autoSubscribe: true,
    };
  }, []);

  React.useEffect(() => {
    room.on(RoomEvent.Disconnected, handleOnLeave);
    room.on(RoomEvent.EncryptionError, handleEncryptionError);
    room.on(RoomEvent.MediaDevicesError, handleError);

    if (e2eeSetupComplete) {
      room
        .connect(
          props.connectionDetails.serverUrl,
          props.connectionDetails.participantToken,
          connectOptions,
        )
        .catch((error) => {
          handleError(error);
        });
      if (props.userChoices.videoEnabled) {
        room.localParticipant.setCameraEnabled(true).catch((error) => {
          handleError(error);
        });
      }
      if (props.userChoices.audioEnabled) {
        room.localParticipant.setMicrophoneEnabled(true).catch((error) => {
          handleError(error);
        });
      }
    }
    return () => {
      room.off(RoomEvent.Disconnected, handleOnLeave);
      room.off(RoomEvent.EncryptionError, handleEncryptionError);
      room.off(RoomEvent.MediaDevicesError, handleError);
    };
  }, [e2eeSetupComplete, room, props.connectionDetails, props.userChoices]);

  const lowPowerMode = useLowCPUOptimizer(room);

  const handleOnLeave = React.useCallback(() => {
    window.location.href = props.isGuest ? '/' : '/admin/';
  }, [props.isGuest]);
  const handleError = React.useCallback((error: Error) => {
    // Cancelling screen share / device prompt in the browser is not an error:
    // Chrome fires NotAllowedError ("Permission denied by user") — stay silent.
    if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
      console.info('Media request dismissed by user:', error.message);
      return;
    }
    console.error(error);
    alert(`${ru.unexpectedError} ${error.message}`);
  }, []);
  const handleEncryptionError = React.useCallback((error: Error) => {
    console.error(error);
    alert(`${ru.encryptionError} ${error.message}`);
  }, []);

  React.useEffect(() => {
    if (lowPowerMode) {
      console.warn('Low power mode enabled');
    }
  }, [lowPowerMode]);

  return (
    <div className="lk-room-container">
      <RoomContext.Provider value={room}>
        <AgimaVideoConference
          // Every employee gets the panel on every meeting: the AGIMA Bot tab
          // works without the assistant and can turn it on.
          showAssistant={!props.isGuest && !!props.connectionDetails.meetingId}
          assistantEnabled={!props.isGuest && !!props.connectionDetails.assistantEnabled}
          meetingId={props.isGuest ? null : props.connectionDetails.meetingId ?? null}
          chatMessageFormatter={formatChatMessageLinks}
          SettingsComponent={SHOW_SETTINGS_MENU ? SettingsMenu : undefined}
        />
        <DebugMode />
      </RoomContext.Provider>
    </div>
  );
}
